import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import { TemplateBlockEditor } from "@/features/template-block-editor/template-block-editor";
import { useTestCorrectionEditorConfig } from "@/features/test-template/correction-editor-config";
import type { TestTemplateRevisionId } from "@brewbase/db/types";
import {
  CloseConfirmDialog,
  CorrectionHeader,
  CurrentTemplateBar,
  FullScreenDialog,
  ReviewStep,
  ScopeStep,
  type CorrectionPreviewData,
  type CorrectionStep,
} from "./correction-shared";

function TestCorrectionEditor({ workingId }: { workingId: number }) {
  const config = useTestCorrectionEditorConfig(workingId as TestTemplateRevisionId);
  return <TemplateBlockEditor config={config} />;
}

export function TestCorrectionFlow({
  sourceRevisionId,
  title,
  preselectInstanceIds,
  onClose,
}: {
  sourceRevisionId: number;
  title: string;
  preselectInstanceIds: number[] | null;
  onClose: () => void;
}) {
  const ns = trpc.tests.templateCorrections;
  const utils = trpc.useUtils();
  const [step, setStep] = useState<CorrectionStep>("scope");
  const [workingId, setWorkingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number> | null>(null);
  const [forwardPatch, setForwardPatch] = useState(true);
  const [confirmClose, setConfirmClose] = useState(false);
  const [applying, setApplying] = useState(false);
  const applyingRef = useRef(false);
  const canEditTemplate = useCan("test-template:edit");

  const { data: options } = ns.listEligibleInstances.useQuery({ sourceRevisionId });
  const { data: openWorking } = ns.getOpen.useQuery({ sourceRevisionId });
  const startMut = ns.start.useMutation();
  const discardMut = ns.discard.useMutation();
  const commitMut = ns.commit.useMutation();
  const forwardMut = ns.forwardPatch.useMutation();
  const replaceMut = ns.replaceWithCurrent.useMutation();
  const { data: outdated } = ns.isSourceOutdated.useQuery({ sourceRevisionId });
  const [editorKey, setEditorKey] = useState(0);
  const [usedCurrent, setUsedCurrent] = useState(false);
  // Edits loaded from the draft are already in it; there is nothing to patch back.
  const canForwardPatch = canEditTemplate && !usedCurrent;

  const allIds = useMemo(() => (options ?? []).map((o) => o.id), [options]);
  const instancesById = useMemo(
    () => new Map((options ?? []).map((o) => [o.id, o])),
    [options],
  );
  const preselected = useMemo(
    () => (preselectInstanceIds ? allIds.filter((id) => preselectInstanceIds.includes(id)) : allIds),
    [allIds, preselectInstanceIds],
  );
  const preselectedHidden =
    !!options && !!preselectInstanceIds && preselected.length < preselectInstanceIds.length;
  const selectedSet = selected ?? new Set(preselected);
  // Opened from a session or operation: list only its instances, not every
  // instance on the revision.
  const scopeOptions = useMemo(
    () =>
      preselectInstanceIds
        ? options?.filter((o) => preselectInstanceIds.includes(o.id))
        : options,
    [options, preselectInstanceIds],
  );
  const selectedIds = [...selectedSet];

  const previewQuery = ns.preview.useQuery(
    { workingId: workingId ?? 0, instanceIds: selectedIds },
    { enabled: step === "review" && !!workingId },
  );

  const toggle = (id: number) => {
    const next = new Set(selectedSet);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleSession = (ids: number[], on: boolean) => {
    const next = new Set(selectedSet);
    ids.forEach((i) => (on ? next.add(i) : next.delete(i)));
    setSelected(next);
  };

  const goEdit = async () => {
    let id = workingId ?? openWorking ?? null;
    if (!id) {
      try {
        id = await startMut.mutateAsync({ sourceRevisionId });
      } catch (e) {
        toast.error(`Could not start correction: ${(e as Error).message}`);
        return;
      }
    }
    setWorkingId(id);
    setStep("edit");
  };

  const apply = async () => {
    if (!workingId || applyingRef.current) return;
    applyingRef.current = true;
    setApplying(true);
    try {
      await commitMut.mutateAsync({ workingId, instanceIds: selectedIds });
    } catch (e) {
      toast.error(`Apply failed: ${(e as Error).message}`);
      applyingRef.current = false;
      setApplying(false);
      return;
    }
    if (forwardPatch && canForwardPatch) {
      try {
        const report = await forwardMut.mutateAsync({ workingId });
        if (report.applied.length > 0) toast.success(`Draft patched: ${report.applied.join(", ")}`);
      } catch (e) {
        toast.warning(`The template draft was not updated: ${(e as Error).message}`);
      }
    }
    toast.success("Correction applied.");
    onClose();
    void utils.invalidate();
  };

  const loadCurrentTemplate = async () => {
    if (!workingId) return;
    try {
      await replaceMut.mutateAsync({ workingId });
    } catch (e) {
      toast.error(`Could not load the current template: ${(e as Error).message}`);
      return;
    }
    setUsedCurrent(true);
    void utils.invalidate();
    setEditorKey((k) => k + 1);
  };

  const discard = async () => {
    if (workingId) {
      try { await discardMut.mutateAsync({ workingId }); } catch { /* ignore */ }
    }
    onClose();
  };

  const preview = previewQuery.data as CorrectionPreviewData | undefined;
  const dataLoss = (preview?.summary.valuesLost ?? 0) > 0;
  const canApply = !!preview && preview.blockers.length === 0 && selectedIds.length > 0;

  const requestClose = (o: boolean) => {
    if (o || applying) return;
    if (workingId) setConfirmClose(true);
    else onClose();
  };

  return (
    <>
    <FullScreenDialog open onOpenChange={requestClose}>
      <CorrectionHeader title="Correct test template" subtitle={title} step={step} />
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {step === "scope" && (
          <ScopeStep options={scopeOptions} selected={selectedSet} onToggle={toggle} onToggleSession={toggleSession} preselectedHidden={preselectedHidden} />
        )}
        {step === "edit" && workingId && (
          <div className="flex-1 min-h-0 flex flex-col">
            <CurrentTemplateBar
              outdatedMessage={outdated && !usedCurrent ? "These tests use an older version of this template." : null}
              pending={replaceMut.isPending}
              onUseCurrent={loadCurrentTemplate}
            />
            <div className="flex-1 min-h-0 overflow-auto">
              <TestCorrectionEditor key={editorKey} workingId={workingId} />
            </div>
          </div>
        )}
        {step === "review" && (
          <ReviewStep preview={preview} instancesById={instancesById} forwardPatch={forwardPatch} setForwardPatch={setForwardPatch} canForwardPatch={canForwardPatch} />
        )}
      </div>
      <div className="flex items-center justify-between border-t border-border px-4 h-12 shrink-0">
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(step === "review" ? "edit" : "scope")} disabled={step === "scope" || applying}>
            Back
          </Button>
          {workingId && (
            <Button variant="ghost" size="sm" className="text-destructive" onClick={discard} disabled={applying}>
              Discard
            </Button>
          )}
        </div>
        {step === "scope" && (
          <Button size="sm" onClick={goEdit} disabled={selectedIds.length === 0}>
            {openWorking ? "Resume editing" : "Start editing"}
          </Button>
        )}
        {step === "edit" && (
          <Button
            size="sm"
            onClick={() => {
              utils.tests.templateCorrections.preview.invalidate();
              setStep("review");
            }}
          >
            Review changes
          </Button>
        )}
        {step === "review" && (
          <Button
            size="sm"
            onClick={apply}
            disabled={!canApply || applying}
            className={dataLoss ? "bg-red-600 text-white hover:bg-red-700" : undefined}
          >
            {applying ? "Applying…" : dataLoss ? "Apply (with data loss)" : "Apply correction"}
          </Button>
        )}
      </div>
    </FullScreenDialog>
    <CloseConfirmDialog
      open={confirmClose}
      onKeep={onClose}
      onDiscard={discard}
      onCancel={() => setConfirmClose(false)}
    />
    </>
  );
}
